import type { RouteRecord } from 'vite-react-ssg'
import RootLayout from './components/RootLayout'
import EditorialLayout from './components/nala/EditorialLayout'
import PortalLayout from './components/PortalLayout'
import EditorialHomePage from './pages/EditorialHomePage'
import LocationsPage from './pages/LocationsPage'
import RegisterPage from './pages/RegisterPage'
import LoginPage from './pages/LoginPage'
import BookingPage from './pages/BookingPage'
import BookAppointmentPage from './pages/BookAppointmentPage'
import ProfilePage from './pages/ProfilePage'
import FAQPage from './pages/FAQPage'
import BlogPage from './pages/BlogPage'
import QuotePage from './pages/QuotePage'
import OAuthCallbackPage from './pages/OAuthCallbackPage'
import { PrivacyPolicyPage, TermsPage, DataPolicyPage } from './pages/LegalPage'
import NotFoundPage from './pages/NotFoundPage'

// Portal pages — eagerly imported so vite-react-ssg prerenders them to
// flat HTML at /portal/sign-in.html, /portal/dashboard.html, etc.
//
// History: these used to be `lazy:` for code-splitting, but lazy routes
// aren't included in the SSG prerender pass, which meant nginx fell
// through to /index.html (the home page) for /portal/sign-in. The SPA
// would then hydrate the home-page DOM as the sign-in page, throwing
// React errors #418/#423/#425 (hydration mismatch). Eager imports fix
// that at the cost of a few KB extra in the initial JS bundle — a fine
// trade for the auth pages that need to load fast and clean.
//
// Dynamic-param routes (`/portal/track/:token`, `/portal/appointments/:id`)
// stay lazy because vite-react-ssg can't prerender them without an
// explicit getStaticPaths, and direct URL hits to them are rare (the
// only entry is post-auth via React Router or a one-time emailed link).
import PortalSignInPage from './pages/portal/SignInPage'
import PortalAuthCallbackPage from './pages/portal/AuthCallbackPage'
import PortalDashboardPage from './pages/portal/DashboardPage'
import PortalPetsPage from './pages/portal/PetsPage'
import PortalAppointmentsPage from './pages/portal/AppointmentsPage'
import PortalInvoicesPage from './pages/portal/InvoicesPage'

// vite-react-ssg lazy routes expect a module that exports `Component`.
// Our portal pages use `export default`, so this adapter rewrites the
// promise to satisfy the contract.
function lazyDefault<P extends { default: React.ComponentType<unknown> }>(
  importer: () => Promise<P>,
) {
  return async () => {
    const mod = await importer()
    return { Component: mod.default }
  }
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // OAuth callback lives outside the marketing layout — no header,
      // no footer, no floating SMS — so the post-auth redirect is clean.
      {
        path: 'auth/callback',
        element: <OAuthCallbackPage />,
      },

      // ── Public marketing + auth pages ───────────────────────────────────
      // EditorialLayout owns the editorial header/footer and the floating
      // SMS launcher + booking modal, so every public page can pop them
      // via the `nala-open-booking` / `nala-open-sms` window events. The
      // inner pages (login/register/book/faq/blog/etc.) keep their
      // existing internal styling for now — they sit inside the new
      // editorial chrome until they get a dedicated reskin pass.
      {
        element: <EditorialLayout />,
        children: [
          { index: true,                  element: <EditorialHomePage /> },
          { path: 'locations',            element: <LocationsPage /> },
          { path: 'register',             element: <RegisterPage /> },
          { path: 'login',                element: <LoginPage /> },
          { path: 'book',                 element: <BookingPage /> },
          { path: 'book/new',             element: <BookAppointmentPage /> },
          { path: 'profile',              element: <ProfilePage /> },
          { path: 'faq',                  element: <FAQPage /> },
          { path: 'blog',                 element: <BlogPage /> },
          { path: 'quote',                element: <QuotePage /> },
          // Public legal pages — must stay on stable URLs because the
          // OAuth provider consoles (Facebook/Google/Apple) hold these
          // as our registered Privacy / ToS / Data-deletion links.
          // nginx.conf carves them out of the basic-auth gate so app
          // reviewers can fetch them without credentials.
          { path: 'policy',               element: <PrivacyPolicyPage /> },
          { path: 'terms',                element: <TermsPage /> },
          { path: 'data-policy',          element: <DataPolicyPage /> },
          // Service detail pages — lazy-loaded so getStaticPaths is co-located
          {
            path: 'services/:slug',
            lazy: () => import('./pages/ServiceDetailPage'),
          },
          { path: '*',                    element: <NotFoundPage /> },
        ],
      },

      // ── Customer portal (own layout, signed-in customers only) ────────
      // Portal pages without a sidebar (sign-in, auth callback, track)
      // sit at the top level so they don't share PortalLayout chrome.
      { path: 'portal/sign-in',         element: <PortalSignInPage /> },
      { path: 'portal/auth/callback',   element: <PortalAuthCallbackPage /> },
      // Track page renders its own full-bleed map UI without the sidebar.
      // Dynamic param — kept lazy because SSG can't prerender it.
      { path: 'portal/track/:token',    lazy: lazyDefault(() => import('./pages/portal/TrackPage')) },

      // Authed portal screens share the PortalLayout chrome.
      {
        element: <PortalLayout />,
        children: [
          { path: 'portal',              element: <PortalDashboardPage /> },
          { path: 'portal/dashboard',    element: <PortalDashboardPage /> },
          { path: 'portal/pets',         element: <PortalPetsPage /> },
          { path: 'portal/appointments', element: <PortalAppointmentsPage /> },
          { path: 'portal/appointments/:id', lazy: lazyDefault(() => import('./pages/portal/AppointmentDetailPage')) },
          { path: 'portal/invoices',     element: <PortalInvoicesPage /> },
        ],
      },
    ],
  },
]
